import { supabase } from '../config/supabase';
import { Ticket, CreateTicketRequest, UpdateTicketRequest } from '../types/ticket';

export const ticketService = {
  // Fetch all tickets
  async getAllTickets(): Promise<Ticket[]> {
    console.log('Fetching all tickets...');
    
    const { data, error } = await supabase
      .from('tickets')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tickets:', error);
      throw error;
    }

    console.log('Fetched tickets:', data);
    return data || [];
  },

  // Fetch tickets by email
  async getTicketsByEmail(email: string): Promise<Ticket[]> {
    console.log('Fetching tickets for email:', email);
    
    const { data, error } = await supabase
      .from('tickets')
      .select('*')
      .eq('email', email)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tickets by email:', error);
      throw error;
    }

    console.log('Fetched tickets for email:', data);
    return data || [];
  },

  // Fetch ticket by ID
  async getTicketById(id: string): Promise<Ticket | null> {
    console.log('Fetching ticket by ID:', id);
    
    const { data, error } = await supabase
      .from('tickets')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching ticket by ID:', error);
      throw error;
    }

    console.log('Fetched ticket:', data);
    return data;
  },

  // Create a new ticket
  async createTicket(ticketData: CreateTicketRequest): Promise<Ticket> {
    console.log('Creating new ticket:', ticketData);
    
    const { data, error } = await supabase
      .from('tickets')
      .insert([ticketData])
      .select()
      .single();

    if (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }

    console.log('Created ticket:', data);
    return data;
  },

  // Update an existing ticket
  async updateTicket(id: string, updates: UpdateTicketRequest): Promise<Ticket> {
    console.log('Updating ticket:', id, 'with updates:', updates);
    
    const { data, error } = await supabase
      .from('tickets')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }

    console.log('Updated ticket:', data);
    return data;
  },

  // Delete a ticket
  async deleteTicket(id: string): Promise<void> {
    console.log('Deleting ticket:', id);
    
    const { error } = await supabase
      .from('tickets')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting ticket:', error);
      throw error;
    }

    console.log('Successfully deleted ticket:', id);
  },

  // Fetch tickets by severity
  async getTicketsBySeverity(severity: 'low' | 'medium' | 'high' | 'critical' | 'suggestion'): Promise<Ticket[]> {
    console.log('Fetching tickets by severity:', severity);
    
    const { data, error } = await supabase
      .from('tickets')
      .select('*')
      .eq('severity', severity)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tickets by severity:', error);
      throw error;
    }

    console.log('Fetched tickets by severity:', data);
    return data || [];
  },

  // Get ticket statistics
  async getTicketStats(): Promise<{
    total: number;
    bySeverity: { [key: string]: number };
  }> {
    console.log('Fetching ticket statistics...');
    
    const { data, error } = await supabase
      .from('tickets')
      .select('severity');

    if (error) {
      console.error('Error fetching ticket statistics:', error);
      throw error;
    }

    const total = data?.length || 0;
    const bySeverity = data?.reduce((acc, ticket) => {
      acc[ticket.severity] = (acc[ticket.severity] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number }) || {};

    const stats = { total, bySeverity };
    console.log('Ticket statistics:', stats);
    return stats;
  }
}; 